import { Select } from 'antd';
import {
  productBoxByCategory,
  productBoxOptions,
  PmsProductBoxOption,
} from 'apis/product-box';
import { FormLabel } from '../form-label';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { CreateProductFormSchemaType } from '../common';
import i18n from 'i18n';
import LOCALS from 'commons/locals';

const formatBoxLabel = (box: PmsProductBoxOption) =>
  `${box.displayName}（${box.lengthCm} × ${box.widthCm} × ${box.heightCm} cm）`;

const ProductBox = ({
  control,
  setValue,
}: {
  control: Control<CreateProductFormSchemaType>;
  setValue: UseFormSetValue<CreateProductFormSchemaType>;
}) => {
  const boxId = useWatch({ control, name: 'boxId' });
  const productCategoryIdList = useWatch({
    control,
    name: 'productCategoryIdList',
  });
  const categoryId = productCategoryIdList?.[productCategoryIdList.length - 1];
  const [options, setOptions] = useState<PmsProductBoxOption[]>([]);
  const [categoryBox, setCategoryBox] = useState<PmsProductBoxOption | null>(
    null,
  );

  useEffect(() => {
    productBoxOptions().then(setOptions);
  }, []);

  useEffect(() => {
    if (!categoryId) {
      setCategoryBox(null);
      return;
    }
    let cancelled = false;
    productBoxByCategory(categoryId).then((box) => {
      if (!cancelled) setCategoryBox(box);
    });
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const selectOptions = useMemo(() => {
    const list = [...options];
    if (categoryBox && !list.some((item) => item.id === categoryBox.id)) {
      list.unshift(categoryBox);
    }
    return list.map((item) => ({
      value: item.id,
      label: formatBoxLabel(item),
    }));
  }, [categoryBox, options]);

  const shownValue = boxId ?? categoryBox?.id;

  return (
    <div className="flex items-center gap-2">
      <FormLabel>Box:</FormLabel>
      <Controller
        name="boxId"
        control={control}
        render={() => (
          <div className="w-full">
            <Select
              className="w-full"
              allowClear
              placeholder={i18n.t(LOCALS.product_box_select_placeholder)}
              options={selectOptions}
              value={shownValue}
              onChange={(value) => {
                setValue('boxId', value ?? null, { shouldDirty: true });
              }}
              onClear={() => {
                setValue('boxId', null, { shouldDirty: true });
              }}
            />
            {boxId == null && categoryBox && (
              <p className="mt-1 text-xs text-gray-500">
                {i18n.t(LOCALS.product_box_category_default_hint)}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default ProductBox;
